import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { adminListUsers, adminCreateUser, adminUpdateUser, adminDeleteUser, adminMe } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Trash2, Lock, Ban, RotateCcw, MapPin, ShieldCheck, Eye } from "lucide-react";

const TOKEN_KEY = "ice_admin_token";
const LOCATIONS = ["Sligo", "Clonmel", "all"];
const ROLES = ["admin", "readonly"];

export default function AdminUsersPage() {
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "readonly", location: "Sligo" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [meRes, list] = await Promise.all([adminMe(token), adminListUsers(token)]);
        setMe(meRes);
        setUsers(list);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Failed to load users");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const availableLocations = me?.location === "all" ? LOCATIONS : [me?.location].filter(Boolean);

  const refresh = async () => {
    try {
      const list = await adminListUsers(token);
      setUsers(list);
    } catch {
      toast.error("Failed to refresh");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminCreateUser(token, form);
      toast.success("User created");
      setForm({ email: "", password: "", name: "", role: "readonly", location: availableLocations[0] || "Sligo" });
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const toggleDisabled = async (u) => {
    const next = !u.disabled_at;
    if (!window.confirm(`${next ? "Disable" : "Re-enable"} ${u.email}?`)) return;
    try {
      await adminUpdateUser(token, u.id, { disabled: next });
      toast.success(next ? "Disabled" : "Re-enabled");
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update");
    }
  };

  const changeRole = async (u, role) => {
    try {
      await adminUpdateUser(token, u.id, { role });
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to change role");
    }
  };

  const changeLocation = async (u, location) => {
    try {
      await adminUpdateUser(token, u.id, { location });
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to change location");
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 chars):`);
    if (!pw) return;
    try {
      await adminUpdateUser(token, u.id, { password: pw });
      toast.success("Password reset");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to reset password");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try {
      await adminDeleteUser(token, u.id);
      toast.success("User deleted");
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to delete");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <Link to="/admin" className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 inline-block">
            Go to admin login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1">Access control</div>
            <h1 className="font-display text-4xl md:text-5xl uppercase">Admin users</h1>
          </div>
          <Link
            to="/admin"
            className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
            data-testid="users-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Dashboard
          </Link>
        </div>

        {/* Create form */}
        <form onSubmit={submit} className="border-2 border-slate-900 bg-white brutal-shadow p-5 mb-8" data-testid="user-create-form">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-4 h-4 text-sky-600" strokeWidth={2.5} />
            <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500">Create user</div>
          </div>
          <div className="grid md:grid-cols-5 gap-3">
            <input required type="text" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
              data-testid="user-name-input" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500 md:col-span-1"
              data-testid="user-email-input" />
            <input required type="password" placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
              data-testid="user-password-input" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
              data-testid="user-role-select">
              {ROLES.map((r) => <option key={r} value={r}>{r === "admin" ? "Full access" : "Read-only"}</option>)}
            </select>
            <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
              data-testid="user-location-select">
              {availableLocations.map((l) => <option key={l} value={l}>{l === "all" ? "All areas (super)" : l}</option>)}
            </select>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="submit" disabled={creating}
              className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-5 py-2.5 text-sm disabled:opacity-60"
              data-testid="user-create-submit">
              {creating ? "Creating…" : "Create user"}
            </button>
          </div>
        </form>

        {/* List */}
        <div className="border-2 border-slate-900 bg-white brutal-shadow" data-testid="users-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-900 text-white font-mono-tight uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left px-3 py-2.5">Name</th>
                <th className="text-left px-3 py-2.5">Email</th>
                <th className="text-left px-3 py-2.5">Role</th>
                <th className="text-left px-3 py-2.5">Location</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-left px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center p-10 font-mono-tight uppercase tracking-widest">Loading…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="text-center p-10 font-mono-tight uppercase tracking-widest text-slate-500">No users yet</td></tr>
              ) : (
                users.map((u) => {
                  const isSelf = me?.id === u.id;
                  const disabled = !!u.disabled_at;
                  return (
                    <tr key={u.id} className={`border-t-2 border-slate-900 ${disabled ? "opacity-60" : ""}`} data-testid={`user-row-${u.email}`}>
                      <td className="px-3 py-2.5">{u.name || "—"} {isSelf && <span className="text-[10px] text-sky-600 ml-1">(you)</span>}</td>
                      <td className="px-3 py-2.5 font-mono-tight">{u.email}</td>
                      <td className="px-3 py-2.5">
                        <select disabled={isSelf}
                          value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                          className="border-2 border-slate-900 p-1 font-mono-tight text-xs bg-white disabled:opacity-60"
                          data-testid={`user-role-${u.email}`}>
                          {ROLES.map((r) => <option key={r} value={r}>{r === "admin" ? "Full" : "Read-only"}</option>)}
                        </select>
                        {u.role === "admin" ? <ShieldCheck className="inline w-3 h-3 text-sky-600 ml-1" strokeWidth={3} /> : <Eye className="inline w-3 h-3 text-amber-700 ml-1" strokeWidth={3} />}
                      </td>
                      <td className="px-3 py-2.5">
                        <select value={u.location} onChange={(e) => changeLocation(u, e.target.value)}
                          className="border-2 border-slate-900 p-1 font-mono-tight text-xs bg-white"
                          data-testid={`user-location-${u.email}`}>
                          {availableLocations.map((l) => <option key={l} value={l}>{l === "all" ? "All" : l}</option>)}
                        </select>
                        <MapPin className="inline w-3 h-3 text-slate-500 ml-1" strokeWidth={3} />
                      </td>
                      <td className="px-3 py-2.5">
                        {disabled ? (
                          <span className="bg-slate-900 text-white px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest border-2 border-slate-900">Disabled</span>
                        ) : (
                          <span className="bg-emerald-500 text-white px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest border-2 border-slate-900">Active</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => resetPassword(u)} title="Reset password"
                            className="bg-white text-slate-900 border-2 border-slate-900 hover:bg-sky-500 hover:text-white p-1.5"
                            data-testid={`user-reset-pw-${u.email}`}>
                            <Lock className="w-3 h-3" strokeWidth={2.5} />
                          </button>
                          {!isSelf && (
                            <button onClick={() => toggleDisabled(u)} title={disabled ? "Re-enable" : "Disable"}
                              className={`border-2 border-slate-900 p-1.5 ${disabled ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-white text-slate-900 hover:bg-amber-500 hover:text-white"}`}
                              data-testid={`user-toggle-disabled-${u.email}`}>
                              {disabled ? <RotateCcw className="w-3 h-3" strokeWidth={2.5} /> : <Ban className="w-3 h-3" strokeWidth={2.5} />}
                            </button>
                          )}
                          {!isSelf && (
                            <button onClick={() => removeUser(u)} title="Delete"
                              className="bg-white text-slate-900 border-2 border-slate-900 hover:bg-red-500 hover:text-white p-1.5"
                              data-testid={`user-delete-${u.email}`}>
                              <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
